import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { provideState, injectState } from 'freactal'
import app from '../../../../feathers'
import {
  getQuestion,
  getSection,
  serializeContentSection,
  deserializeContentSection,
  sectionIndexToPath,
} from '../../../../lib'

import QuestionContentDialog from './QuestionContentDialog'

const withState = provideState({
  initialState: () => ({
    addQuestionError: null,
    updateQuestionError: null,
    deleteQuestionError: null,
  }),
  effects: {
    addQuestion: async (effects, { _id, sectionIndex, question }) => {
      try {
        const sectionPath = sectionIndexToPath(sectionIndex)
        await app.service('units').patch(_id, {
          $push: {
            [`sections.${sectionPath}.questions`]: question,
          },
        })
      } catch (addQuestionError) {
        return state => ({ ...state, addQuestionError })
      }
    },
    updateQuestion: async (
      effects,
      { _id, sectionIndex, questionIndex, question },
    ) => {
      try {
        const sectionPath = sectionIndexToPath(sectionIndex)
        await app.service('units').patch(_id, {
          [`sections.${sectionPath}.questions.${questionIndex}`]: question,
        })
      } catch (updateQuestionError) {
        return state => ({ ...state, updateQuestionError })
      }
    },
    deleteQuestion: async (effects, { _id, sectionIndex, questionId }) => {
      try {
        const sectionPath = sectionIndexToPath(sectionIndex)
        await app.service('units').patch(_id, {
          $pull: {
            [`sections.${sectionPath}.questions`]: { _id: questionId },
          },
        })
      } catch (deleteQuestionError) {
        return state => ({ ...state, deleteQuestionError })
      }
    },
  },
})

class QuestionContent extends Component {
  static contextTypes = {
    prompt: PropTypes.func,
  }
  state = {
    saving: false,
    ...this.getInitialQuestionContent(),
  }
  getInitialQuestionContent() {
    const {
      match: { params: { sectionIndex, questionIndex } },
      location: { state: { newQuestion } = {} },
    } = this.props
    if (!questionIndex && newQuestion) {
      return {
        questionType: 'review',
        questionText: deserializeContentSection(),
        scenario: deserializeContentSection(),
        // initialise learner answer area with 8 blank lines
        learnerAnswerSection: deserializeContentSection({
          nodes: Array(8).fill({
            kind: 'block',
            type: 'paragraph',
            nodes: [
              {
                kind: 'text',
                text: '',
              },
            ],
          }),
        }),
        answer: deserializeContentSection(),
      }
    }
    const {
      questionText,
      scenario,
      learnerAnswerSection,
      answer,
      type,
      multiChoiceOptions,
    } = getQuestion(this.props.state.unit, sectionIndex, questionIndex)
    return {
      questionType: type,
      questionText: deserializeContentSection(questionText),
      scenario: deserializeContentSection(scenario),
      learnerAnswerSection: deserializeContentSection(learnerAnswerSection),
      answer: deserializeContentSection(answer),
      multiChoiceOptions: multiChoiceOptions,
    }
  }
  handleChange = (type, state) => {
    this.setState({
      [type]: state,
    })
  }
  handleChangeQuestionType = type => this.handleChange('questionType', type)

  /* Multiple Choice Logic */
  handleMultiChoiceOption = options => this.handleChange('multiChoiceOptions', options)
  handleAddMultiChoiceOption = () => {
    const {
      multiChoiceOptions
    } = this.state;
    const newOptions = multiChoiceOptions || []
    newOptions.push({ text: '', isAnswer: false, index: newOptions.length + 1 })
    this.handleChange('multiChoiceOptions', newOptions)
  }
  // handleOptionUpdated = option => {
  //   const {
  //     multiChoiceOptions
  //   } = this.state;
  //   multiChoiceOptions = multiChoiceOptions.map(x => x.index === option.index ? option : x)
  //   this.handleChange('multiChoiceOptions', newOptions)
  // }


  handleChangeQuestionText = state => this.handleChange('questionText', state)
  handleChangeAnswer = state => this.handleChange('answer', state)
  handleChangeLearnerAnswerSection = state =>
    this.handleChange('learnerAnswerSection', state)
  handleChangeScenario = state => this.handleChange('scenario', state)
  handleDelete = async () => {
    const {
      match: { params: { sectionIndex, questionIndex } },
      effects: { deleteQuestion },
      state: { unit: { _id: unitId } },
    } = this.props
    const response = await this.context.prompt({
      title: `Delete Question ${parseInt(questionIndex, 10) + 1}`,
      message: 'Are you sure you want to delete this?',
      options: ['Delete'],
    })
    if (response !== 'Delete') return
    const { _id: questionId } = getQuestion(
      this.props.state.unit,
      sectionIndex,
      questionIndex,
    )
    deleteQuestion({
      _id: unitId,
      sectionIndex,
      questionId,
    })
    this.props.goBack()
  }
  handleSave = async () => {
    const {
      match: { params: { sectionIndex, questionIndex } },
      location: { state: { newQuestion } = {} },
      state: { unit },
      effects: { addQuestion, updateQuestion },
    } = this.props
    const {
      questionType: type,
      questionText,
      scenario,
      learnerAnswerSection,
      answer,
      multiChoiceOptions,
    } = this.state
    this.setState({ saving: true })
    const question = {
      type,
      questionText: serializeContentSection(questionText),
      scenario: serializeContentSection(scenario),
      learnerAnswerSection: serializeContentSection(learnerAnswerSection),
      answer: serializeContentSection(answer),
      multiChoiceOptions,
    }
    if (newQuestion) {
      const newQuestionIndex = getSection(unit, sectionIndex).questions.length
      await addQuestion({
        _id: unit._id,
        sectionIndex,
        question,
      })
      this.props.history.push(`./${newQuestionIndex}/mapping`)
    } else {
      await updateQuestion({
        _id: unit._id,
        sectionIndex,
        questionIndex,
        question,
      })
    }
    this.setState({ saving: false })
  }
  render() {
    const { history, location: { state: { newQuestion } = {} } } = this.props
    const {
      questionType,
      questionText,
      scenario,
      learnerAnswerSection,
      answer,
      multiChoiceOptions,
      saving,
    } = this.state
    const isMultipleChoice = (questionType.indexOf('-multi') > 0)
    return (
      <QuestionContentDialog
        goBack={history.goBack}
        questionType={questionType}
        onSave={this.handleSave}
        onDelete={this.handleDelete}
        canDelete={!newQuestion}
        onChangeQuestionType={this.handleChangeQuestionType}
        onChangeQuestionText={this.handleChangeQuestionText}
        onChangeAnswer={this.handleChangeAnswer}
        onChangeLearnerAnswerSection={this.handleChangeLearnerAnswerSection}
        onChangeScenario={this.handleChangeScenario}
        onChangeMultiChoiceOption={this.handleMultiChoiceOption}
        onAddMultiChoiceOption={this.handleAddMultiChoiceOption}
        onMultiChoiceOptionChanged
        displaySpinner={saving}
        content={{
          questionText,
          scenario,
          learnerAnswerSection,
          answer,
          multiChoiceOptions,
        }}
      />
    )
  }
}

export default withState(injectState(QuestionContent))



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/LearnerGuide/SectionContent/QuestionContent.js